import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Send } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = (err) =>
  `w-full rounded-lg border bg-white/[.04] px-3 py-2 font-mono text-xs text-slate-100 outline-none transition-colors duration-150 placeholder:text-slate-600 focus:border-sonoma/50 ${err ? "border-red-400/50" : "border-white/10"}`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorDetail, setErrorDetail] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "at least 2 characters";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "enter a valid email";
    if (form.message.trim().length < 10) errs.message = "at least 10 characters";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    setErrorDetail("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          website: form.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not send — please try again");
      setStatus("success");
    } catch (err) {
      setErrorDetail(err.message || "Network error — please try again");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-green-400/25 bg-green-400/10 p-5 text-center" data-testid="contact-form-success">
        <CheckCircle2 size={20} className="text-green-400" />
        <p className="font-mono text-sm text-green-300">Message sent — it landed in Anish&apos;s inbox.</p>
        <button
          onClick={() => {
            setForm({ name: "", email: "", message: "", website: "" });
            setStatus("idle");
          }}
          data-testid="contact-form-reset"
          className="font-mono text-[11px] text-slate-400 underline transition-colors duration-150 hover:text-slate-200"
        >
          send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="contact-form" noValidate>
      <p className="eyebrow">./contact.sh --write</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">your name</span>
          <input
            value={form.name}
            onChange={set("name")}
            data-testid="contact-form-name"
            placeholder="Ada Lovelace"
            aria-label="Your name"
            className={inputCls(errors.name)}
          />
          {errors.name ? <span className="mt-1 block font-mono text-[10px] text-red-400">{errors.name}</span> : null}
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">your email</span>
          <input
            value={form.email}
            onChange={set("email")}
            data-testid="contact-form-email"
            type="email"
            placeholder="you@example.com"
            aria-label="Your email"
            className={inputCls(errors.email)}
          />
          {errors.email ? <span className="mt-1 block font-mono text-[10px] text-red-400">{errors.email}</span> : null}
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">message</span>
        <textarea
          value={form.message}
          onChange={set("message")}
          data-testid="contact-form-message"
          rows={4}
          placeholder="Hi Anish — I saw your reconciliation work and wanted to talk…"
          aria-label="Your message"
          className={`${inputCls(errors.message)} resize-none`}
        />
        {errors.message ? <span className="mt-1 block font-mono text-[10px] text-red-400">{errors.message}</span> : null}
      </label>
      {/* honeypot — hidden from humans, traps bots */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      {status === "error" ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-400/25 bg-red-400/10 p-3" data-testid="contact-form-error">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-400" />
          <p className="font-mono text-[11px] leading-relaxed text-red-300">{errorDetail}</p>
        </div>
      ) : null}
      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="contact-form-submit"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-sonoma px-4 py-3 font-mono text-xs font-medium text-white transition-colors duration-150 hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "sending" ? (
          <>
            <Loader2 size={13} className="animate-spin" /> sending…
          </>
        ) : (
          <>
            <Send size={13} /> send message
          </>
        )}
      </button>
    </form>
  );
}
